import React, { useEffect, useState } from 'react';
import { Input, Modal, ModalBody, ModalFooter, ModalHeader } from "reactstrap";
import { useTranslation } from 'react-i18next';
import { useDispatch, useSelector } from "react-redux";
import { createSelector } from "reselect";
import { updateEventSongNote } from "../../slices/event/song/note/thunk";
import { eventSongNoteReset } from "../../slices/event/song/note/reducer";
import { viewEventSongs } from "../../slices/event/view/thunk";

const MAX_NOTE_LENGTH = 150;
const MIN_NOTE_LENGTH = 2;

/**
 * Lets a guest write or change the dedication of a song already on the list.
 * The song is only editable from the second position down - the caller decides
 * whether to render the button, the API checks the position again on save.
 */
export const EditSongNoteModal = ({ eventSong, eventId, guestId, isKaraoke, onClose }) => {
  const { t } = useTranslation();
  const dispatch = useDispatch();
  const [note, setNote] = useState('');
  const [error, setError] = useState(null);

  const selectEventSongNote = createSelector(
    (state) => state.EventSongNote,
    (slice) => ({
      noteSuccess: slice.success,
      noteError: slice.errorMessage,
      noteSaving: slice.saving,
    })
  );
  const { noteSuccess, noteError, noteSaving } = useSelector(selectEventSongNote);

  useEffect(() => {
    setNote(eventSong?.guestNote || '');
    setError(null);
  }, [eventSong]);

  useEffect(() => {
    if (noteSuccess) {
      dispatch(eventSongNoteReset());
      dispatch(viewEventSongs(eventId));
      onClose();
    }
  }, [noteSuccess]);

  useEffect(() => {
    if (noteError) {
      setError(t(noteError));
      dispatch(eventSongNoteReset());
    }
  }, [noteError]);

  const handleSave = () => {
    const trimmed = note.trim();

    if (trimmed.length > 0 && trimmed.length < MIN_NOTE_LENGTH) {
      setError(t('MIN_2_CHARS'));
      return;
    }

    // An empty field clears the dedication.
    dispatch(updateEventSongNote(eventSong.id, guestId, trimmed.length > 0 ? trimmed : null));
  };

  return (
    <Modal isOpen={!!eventSong} toggle={onClose} centered>
      <ModalHeader toggle={onClose}>
        {isKaraoke ? t('Note for the Host') : t('Dedication')}
      </ModalHeader>
      <form onSubmit={(e) => { e.preventDefault(); handleSave(); }}>
        <ModalBody>
          <div className="mb-2 text-muted">
            {eventSong?.title} <span className="fw-light">- {eventSong?.artist}</span>
          </div>
          <Input
            type="textarea"
            rows="3"
            maxLength={MAX_NOTE_LENGTH}
            autoFocus
            className={error ? 'is-invalid' : ''}
            placeholder={isKaraoke ? t('Note for the Host') : t('Enter dedication')}
            value={note}
            onChange={(e) => { setNote(e.target.value); setError(null); }}
          />
          {error && <div className="invalid-feedback d-block">{error}</div>}
          <div className="form-text d-flex justify-content-between">
            <span>{t('Leave the field empty to remove the dedication.')}</span>
            <span>{note.length}/{MAX_NOTE_LENGTH}</span>
          </div>
        </ModalBody>
        <ModalFooter>
          <div className="hstack gap-2 justify-content-end">
            <button type="button" className="btn btn-label btn-light" onClick={onClose}>
              <i className="ri-close-circle-line label-icon align-middle fs-16 me-2"></i>{t('Close')}
            </button>
            <button type="submit" className="btn btn-label btn-success color-primary" disabled={noteSaving}>
              <i className="ri-check-double-line label-icon align-middle fs-16 me-2"></i>{t('Save')}
            </button>
          </div>
        </ModalFooter>
      </form>
    </Modal>
  );
};
